/**
 * Editor adapter / capability registry - a CONTRACT/REGISTRY only.
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #4-#6, #10.
 *
 * Describes what DoAR genuinely supports TODAY, kind by kind. It does not
 * migrate or merge any editor implementation - GraphicalSpace, MotionSpace,
 * ModelSpace, GeoSpace and DoARDocumentEditor are untouched. ImmersiveSpace
 * (the old 4D link-out placeholder this file's "immersive-output" notes
 * below used to describe) is deleted - 4D now converges on
 * Universal3DWorkspace's AR Output surface, delegating to the canonical
 * /d/:doarId/ar route (contract: Prompt 3B §5/§9).
 *
 * `status` here is about whether a *working editor* exists for the kind, not
 * about any single asset's own capability tier (that combination happens in
 * representationResolver.js, which is the module allowed to say "this GLB is
 * editable" or "this DWG is source-only").
 *
 * Loader identifiers are opaque strings, not imports - nothing in this file
 * imports Three.js, Cesium, WebXR, or any heavy editor runtime.
 */
import { WORKSPACE_MODES } from "./workspaceMode";

export const EDITOR_STATUS = Object.freeze({
  EDITABLE: "editable",
  PREVIEWABLE: "previewable",
  OUTPUT_ONLY: "output-only",
  SOURCE_ONLY: "source-only",
  UNSUPPORTED: "unsupported",
  PLANNED: "planned",
});

// id -> { mode, kind, status, editorId (opaque loader id, or null), capabilities, notes }
const REGISTRY = Object.freeze({
  board: {
    id: "board",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "board",
    status: EDITOR_STATUS.EDITABLE,
    editorId: "graphical-space",
    capabilities: ["arrange", "place", "zoom-pan", "transform", "order", "undo-redo", "board-revisions", "png-export"],
    notes: "GraphicalSpace's infinite canvas; hosts image/reference/video/pdf/model/doc cards.",
  },
  image: {
    id: "image",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "image",
    status: EDITOR_STATUS.PREVIEWABLE,
    editorId: "graphical-space",
    capabilities: ["place-on-board", "board-annotations", "annotation-snapshot"],
    notes: "Source preview with editable vector annotations saved in board revisions. No crop/mask/pixel-layer adapter; AI replacement is a separate generated source.",
  },
  pdf: {
    id: "pdf",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "pdf",
    status: EDITOR_STATUS.PREVIEWABLE,
    editorId: "asset-viewport",
    capabilities: ["preview"],
    notes: "Previewable via AssetViewport/board; no page/annotation editor exists yet.",
  },
  document: {
    id: "document",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "document",
    status: EDITOR_STATUS.SOURCE_ONLY,
    editorId: null,
    capabilities: [],
    notes:
      "Uploaded DOC/DOCX/PPT/PPTX assets are source-only. A real text-document " +
      "editor (DoARDocumentEditor) exists, but it operates on DoAR objects whose " +
      "kind is 'document', or the exact owned native studio project root, not on " +
      "an arbitrary uploaded document asset - keep that distinction, do not conflate them.",
  },
  video: {
    id: "video",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "video",
    status: EDITOR_STATUS.EDITABLE,
    editorId: "motion-space",
    capabilities: ["trim", "timeline", "clip-export"],
    notes: "MotionSpace edits DIRECT video (mp4/webm/mov). AVI/MKV need REAL_CONVERSION first.",
  },
  audio: {
    id: "audio",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "audio",
    status: EDITOR_STATUS.PREVIEWABLE,
    editorId: "asset-viewport",
    capabilities: ["playback", "seek", "source-preservation"],
    notes: "Browser-supported audio uses the existing audio preview controls. Source audio can be used by CreatorTimeline; no standalone waveform editor is available.",
  },
  drawing: {
    id: "drawing",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "drawing",
    status: EDITOR_STATUS.EDITABLE,
    editorId: "draw-overlay",
    capabilities: ["draw", "erase", "shapes", "undo-redo", "save-png-asset"],
    notes:
      "A real freehand drawing tool exists (components/workspace/DrawOverlay.jsx: " +
      "pencil/brush/eraser/line/rectangle/palette/stroke size, canvas-based, PNG " +
      "output). Universal2DWorkspace admits and registers the PNG through canonical intake, then activates the exact Asset. Strokes have bounded in-session undo; the stored output is a raster image.",
  },
  reference: {
    id: "reference",
    mode: WORKSPACE_MODES.TWO_D,
    kind: "reference",
    status: EDITOR_STATUS.PREVIEWABLE,
    editorId: "graphical-space",
    capabilities: ["place-on-board"],
    notes: "Reference-type board cards; placement only.",
  },
  model: {
    id: "model",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "model",
    status: EDITOR_STATUS.EDITABLE,
    editorId: "model-space",
    capabilities: ["transform", "scene-graph", "material"],
    notes:
      "ModelSpace's RENDERABLE set (spaces/ModelSpace.jsx) actually loads " +
      "glb/gltf/obj/fbx/stl/ply/dae/3ds via three.js loaders. USDZ is in " +
      "ModelSpace's own UPLOAD_ONLY set, not RENDERABLE, and fileTypeMap.js " +
      "marks it browserReady:false - it is handled by the separate " +
      "'model-native-output' kind below, not this one.",
  },
  "model-native-output": {
    id: "model-native-output",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "model-native-output",
    status: EDITOR_STATUS.OUTPUT_ONLY,
    editorId: null,
    capabilities: ["native-ar-delivery"],
    notes:
      "USDZ: a real, usable native AR delivery format (iOS Quick Look, " +
      "reached only through the canonical /d/:doarId/ar SpatialSession " +
      "route - never opened directly) but not loaded or edited by " +
      "ModelSpace's three.js pipeline - DIRECT capability with " +
      "browserReady:false in fileTypeMap.js is exactly this case, not " +
      "browser-editable mesh content and not merely source-only.",
  },
  "cad-source": {
    id: "cad-source",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "cad-source",
    status: EDITOR_STATUS.SOURCE_ONLY,
    editorId: null,
    capabilities: [],
    notes: "SKP/RVT/IFC/DWG/DXF/3DM/Blender are all SOURCE_ONLY today - no provider adapter exists.",
  },
  "pointcloud-source": {
    id: "pointcloud-source",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "pointcloud-source",
    status: EDITOR_STATUS.SOURCE_ONLY,
    editorId: null,
    capabilities: [],
    notes: "LAS/LAZ/E57/PTS/XYZ are SOURCE_ONLY. PLY is DIRECT and handled by the 'model' kind instead.",
  },
  geo: {
    id: "geo",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "geo",
    status: EDITOR_STATUS.EDITABLE,
    editorId: "geo-space",
    capabilities: ["environment", "camera", "time-place"],
    notes: "GeoSpace (Cesium) - environment/camera configuration, not generic mesh editing.",
  },
  "immersive-output": {
    id: "immersive-output",
    mode: WORKSPACE_MODES.THREE_D,
    kind: "immersive-output",
    status: EDITOR_STATUS.OUTPUT_ONLY,
    editorId: null,
    capabilities: [],
    notes: "4D is an AR Output/experience state inside Universal3DWorkspace, not an editor - it delegates to the canonical /d/:doarId/ar route (SpatialSession + WebXRSpatialRuntime), never an arbitrary link-out.",
  },
});

export function getEditorAdapter(kindId) {
  return REGISTRY[kindId] || null;
}

export function listEditorAdapters(mode = null) {
  const all = Object.values(REGISTRY);
  return mode ? all.filter((entry) => entry.mode === mode) : all;
}

export function hasWorkingEditor(kindId) {
  const entry = getEditorAdapter(kindId);
  return entry?.status === EDITOR_STATUS.EDITABLE;
}

export function canOpen(kindId, { mode } = {}) {
  const entry = getEditorAdapter(kindId);
  if (!entry) return false;
  if (mode && entry.mode !== mode) return false;
  return entry.status === EDITOR_STATUS.EDITABLE || entry.status === EDITOR_STATUS.PREVIEWABLE;
}
